import { ColumnList, ColumnListContent, ColumnListTrigger } from "@qw/ui";
import { Link, useParams } from "react-router";
import { useInboxQueryStates } from "@/providers/inbox-query-states-provider";
import { useInboxScope } from "@/providers/inbox-scope";
import { useBook } from "@/providers/use-book";
import { usePage } from "@/providers/use-page";
import { useBookRootPath } from "./book-navigation";
import { BookResultsList } from "./book-results-list";
import { BookRow } from "./book-row";
import type { BookInsured } from "./use-book-insureds";
import { accountSetScope } from "./use-book-scope";
import { useSearchInsureds } from "./use-search-insureds";

const ALL_INSUREDS_LABEL = "All insureds";

export const BookResults = () => {
  const {
    bound,
    bindRequested,
    brokerReview,
    delivered,
    informationGathering,
    lost,
    pending,
    quoted,
    quotedRenewals,
    upcomingRenewals,
    submitted,
    submittedRenewals,
    unsubmitted,
    solicitedUnsubmittedRenewals,
    unsolicitedUnsubmittedRenewals,
    won,
  } = useBook();
  // The committed `?term=` is the only search term the book answers to; the search box keeps its
  // in-progress one to itself until the broker presses Enter.
  const { searchTerm } = useInboxQueryStates();
  const { other } = useSearchInsureds(searchTerm);
  // `search(term)` applies no agency restriction to an internal user, so these rows are
  // QuoteWell-wide and carry no agent to filter on. Under a set-scope chip that would make them the
  // one thing in the rail outside the fence the chip claims, so the band goes rather than lying.
  // The dropdown's insured band is still book-wide, which is where a broker reaches an insured the
  // scope excludes — and picking one drops the scope.
  const scope = useInboxScope();
  const accountSetFence = accountSetScope(scope);
  return (
    <>
      <BookResultsList label="Marketing: Information Gathering" items={informationGathering} />
      <BookResultsList label="Marketing: Broker Review" items={brokerReview} />
      <BookResultsList label="Marketing: Unsubmitted" items={unsubmitted} />
      <BookResultsList label="Marketing: Submitted" items={submitted} />
      <BookResultsList label="Marketing: Quoted" items={quoted} />
      <BookResultsList label="Quote: Pending" items={pending} />
      <BookResultsList label="Quote: Delivered" items={delivered} />
      <BookResultsList label="Quote: Bind Requested" items={bindRequested} />
      <BookResultsList label="Quote: Bound" items={bound} />
      <BookResultsList label="Renewals: Unsolicited" items={unsolicitedUnsubmittedRenewals} variant="renewal" />
      <BookResultsList label="Renewals: Solicited" items={solicitedUnsubmittedRenewals} variant="renewal" />
      <BookResultsList label="Renewals: Submitted" items={submittedRenewals} variant="renewal" />
      <BookResultsList label="Renewals: Quoted" items={quotedRenewals} variant="renewal" />
      <BookResultsList label="Renewals: Upcoming" items={upcomingRenewals} variant="renewal" />
      {Object.keys(won).map((quarter) => (
        <BookResultsList key={quarter} label={`Won ${quarter}`} items={won[quarter]} />
      ))}
      {Object.keys(lost).map((quarter) => (
        <BookResultsList key={quarter} label={`Lost ${quarter}`} items={lost[quarter]} />
      ))}
      {!accountSetFence && other.length > 0 && (
        <ColumnList label={ALL_INSUREDS_LABEL} open={true}>
          <ColumnListTrigger label={ALL_INSUREDS_LABEL} count={other.length} disabled />
          <ColumnListContent>
            {other.map((insured) => (
              <SearchInsuredLink key={insured.id} insured={insured} />
            ))}
          </ColumnListContent>
        </ColumnList>
      )}
    </>
  );
};

/**
 * These insureds came back from `search(term)` rather than from a book list, so there is no state,
 * no effective date and no pin to hang off them — just the name, indented to line up with the book
 * rows above.
 */
const SearchInsuredLink = ({ insured }: { insured: BookInsured }) => {
  const { insuredId } = useParams();
  const { rootPath } = usePage();
  const bookRootPath = useBookRootPath();
  const { onSelectInsured } = useBook();

  return (
    <BookRow>
      <Link
        to={`${bookRootPath ?? rootPath}/${insured.id}`}
        aria-current={insuredId === insured.id}
        className="flex items-center min-w-0 pl-[--spacing(22)] text-xs"
        onClick={() => onSelectInsured?.(insured)}
      >
        <span className="truncate">{insured.name}</span>
      </Link>
    </BookRow>
  );
};
